// Global staleness strip — Layer 2 of the never-stale UI honesty pass.
//
// Reads the shared feed-health poll (AppShell → useFeedHealth, 60 s) and, when
// any feed reports 'stale' or 'offline', shows a thin amber strip directly
// below the console header with the count and a "View status" link. Renders
// nothing when every feed is healthy, so it is invisible in the happy path.
//
// Phones (portrait or landscape): the strip collapses to a 28 px line with the
// count; tapping it opens the detail in a bottom sheet (the feeds, their status
// and age, and the link to Feed health). AppShell's aria-live region announces
// feed-state changes, so the line itself is a plain button.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowRight, ChevronDown, ChevronUp } from "lucide-react";

import type { FeedHealth } from "@/app/console/useFeedHealth";
import { Drawer, StatusDot } from "@/app/console/ui";
import { usePhone } from "@/app/console/useViewport";
import { relTime } from "@/lib/ist";

/** "every 15 min" for numeric cadences; "daily · 96 blocks" / "intraday" as they come. */
function cadenceLabel(c: string): string {
  return /^\d/.test(c.trim()) ? `every ${c.trim()}` : c.trim();
}

export function StalenessBanner({ health }: { health: FeedHealth }) {
  const phone = usePhone();
  const [open, setOpen] = useState(false);
  const n = health.unhealthy.length;

  // Close the sheet if everything recovers while it is open.
  useEffect(() => {
    if (n === 0) setOpen(false);
  }, [n]);

  if (n === 0) return null;

  const offline = health.offline.length;
  const stale = health.stale.length;
  const detail =
    offline && stale ? `${offline} offline · ${stale} stale` : offline ? `${offline} offline` : `${stale} stale`;
  const summary = (
    <>
      <span className="font-mono font-medium tabular-nums">{n}</span> feed{n === 1 ? "" : "s"} need attention
    </>
  );

  if (phone) {
    return (
      <>
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-haspopup="dialog"
          aria-expanded={open}
          className="flex h-7 w-full shrink-0 items-center justify-between gap-2 border-b border-warn/25 bg-warn/[0.08] px-3 text-xs text-warn transition-colors duration-fast hover:bg-warn/[0.12] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand/60"
          style={{ paddingLeft: "max(12px, var(--pb-safe-left))", paddingRight: "max(12px, var(--pb-safe-right))" }}
        >
          <span className="flex min-w-0 items-center gap-1.5">
            <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden="true" />
            <span className="truncate">{summary}</span>
          </span>
          <span className="inline-flex shrink-0 items-center gap-0.5 font-medium">
            Details {open ? <ChevronUp className="h-3 w-3" aria-hidden="true" /> : <ChevronDown className="h-3 w-3" aria-hidden="true" />}
          </span>
        </button>
        <Drawer
          open={open}
          onClose={() => setOpen(false)}
          title="Feed status"
          description={`${detail} · polled every minute`}
          accent="var(--pb-warn)"
          width="sm"
          footer={
            <Link
              to="/data"
              onClick={() => setOpen(false)}
              className="inline-flex h-11 w-full items-center justify-center gap-1.5 rounded-control border border-line-strong text-sm font-semibold text-text-1 transition-colors duration-fast hover:border-white/25 hover:bg-white/[0.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60"
            >
              Open feed health <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          }
        >
          <ul className="divide-y divide-line" aria-label="Feeds needing attention">
            {health.unhealthy.map((f) => (
              <li key={f.id} className="flex min-h-11 items-center gap-3 py-2.5">
                <StatusDot tone={f.status === "offline" ? "danger" : "warn"} pulse={f.status === "offline"} />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm text-text-1">{f.name}</div>
                  <div className="truncate text-xs text-text-3">
                    {f.domain} · {cadenceLabel(f.cadence)}
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  <div className={`text-xs font-medium ${f.status === "offline" ? "text-danger" : "text-warn"}`}>{f.status}</div>
                  <div className="font-mono text-[11px] tabular-nums text-text-3">{f.lastUpdate ? relTime(f.lastUpdate) : "never"}</div>
                </div>
              </li>
            ))}
          </ul>
        </Drawer>
      </>
    );
  }

  return (
    <div
      role="alert"
      className="flex min-h-8 shrink-0 items-center justify-between gap-3 border-b border-warn/25 bg-warn/[0.08] px-4 text-[13px] text-warn sm:px-6"
    >
      <span className="flex min-w-0 items-center gap-2">
        <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        <span className="truncate">
          {summary}
          <span className="text-warn/70"> · {detail}</span>
        </span>
      </span>
      <Link
        to="/data"
        className="-mr-2 inline-flex h-8 shrink-0 items-center gap-1 rounded-control px-2 font-medium text-warn underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60 coarse:h-11"
      >
        View status <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
      </Link>
    </div>
  );
}
